export const ALERT_EVENT_NAME = "hotel:departmentAlertReceived";

export const HOTEL_DEPARTMENTS = Object.freeze({
  reception: "Reception",
  housekeeping: "Housekeeping",
  maintenance: "Maintenance",
  roomDining: "RoomDining",
  security: "Security",
  emergency: "Emergency",
  other: "Other",
  all: "AllDepartments",
});

export const CANONICAL_DEPARTMENTS = Object.freeze([
  HOTEL_DEPARTMENTS.reception,
  HOTEL_DEPARTMENTS.housekeeping,
  HOTEL_DEPARTMENTS.maintenance,
  HOTEL_DEPARTMENTS.roomDining,
  HOTEL_DEPARTMENTS.security,
  HOTEL_DEPARTMENTS.emergency,
  HOTEL_DEPARTMENTS.other,
]);

export const ALERT_PRIORITIES = {
  urgent: "Urgent",
  high: "High",
  normal: "Normal",
  low: "Low",
};

const normalizeDepartmentToken = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const safeText = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || fallback;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    const text = value
      .map((item) => safeText(item))
      .filter(Boolean)
      .join(", ");

    return text || fallback;
  }

  if (typeof value === "object") {
    return (
      safeText(value.note) ||
      safeText(value.Note) ||
      safeText(value.message) ||
      safeText(value.Message) ||
      safeText(value.body) ||
      safeText(value.Body) ||
      safeText(value.title) ||
      safeText(value.Title) ||
      safeText(value.description) ||
      safeText(value.Description) ||
      safeText(value.name) ||
      safeText(value.Name) ||
      safeText(value.requestTypeName) ||
      safeText(value.RequestTypeName) ||
      fallback
    );
  }

  return fallback;
};

const firstText = (...values) => {
  for (const value of values) {
    const text = safeText(value);
    if (text) return text;
  }

  return "";
};

export const normalizeDepartment = (
  value,
  fallback = HOTEL_DEPARTMENTS.reception
) => {
  const normalizedFallback = normalizeDepartmentToken(fallback);
  const safeFallback = (() => {
    switch (normalizedFallback) {
      case "all":
      case "allalerts":
      case "alldepartments":
      case "operations":
      case "admin":
      case "manager":
        return HOTEL_DEPARTMENTS.all;
      case "frontdesk":
      case "frontoffice":
      case "reception":
        return HOTEL_DEPARTMENTS.reception;
      case "housekeeping":
      case "roomcleaning":
      case "cleaning":
      case "clean":
      case "linen":
      case "towel":
      case "towels":
        return HOTEL_DEPARTMENTS.housekeeping;
      case "maintenance":
      case "repair":
      case "repairs":
      case "fix":
      case "fixing":
      case "broken":
      case "wifi":
      case "wifiissue":
      case "internet":
        return HOTEL_DEPARTMENTS.maintenance;
      case "roomdining":
      case "roomservice":
      case "food":
      case "foodrestaurant":
      case "restaurant":
      case "dining":
      case "meal":
        return HOTEL_DEPARTMENTS.roomDining;
      case "security":
        return HOTEL_DEPARTMENTS.security;
      case "emergency":
      case "urgent":
      case "sos":
      case "emergencyhelp":
      case "urgentstaffassistance":
        return HOTEL_DEPARTMENTS.emergency;
      case "other":
      case "traveldesk":
      case "travel":
      case "taxi":
      case "taxirequest":
      case "transport":
        return HOTEL_DEPARTMENTS.other;
      default:
        return HOTEL_DEPARTMENTS.other;
    }
  })();

  if (value === null || value === undefined || String(value).trim() === "") {
    return safeFallback;
  }

  const trimmed = String(value).trim();

  switch (normalizeDepartmentToken(trimmed)) {
    case "all":
    case "allalerts":
    case "alldepartments":
    case "operations":
    case "admin":
    case "manager":
      return HOTEL_DEPARTMENTS.all;
    case "frontdesk":
    case "frontoffice":
    case "reception":
      return HOTEL_DEPARTMENTS.reception;
    case "housekeeping":
    case "roomcleaning":
    case "cleaning":
    case "clean":
    case "linen":
    case "towel":
    case "towels":
      return HOTEL_DEPARTMENTS.housekeeping;
    case "maintenance":
    case "repair":
    case "repairs":
    case "fix":
    case "fixing":
    case "broken":
    case "wifi":
    case "wifiissue":
    case "internet":
      return HOTEL_DEPARTMENTS.maintenance;
    case "roomdining":
    case "roomservice":
    case "food":
    case "foodrestaurant":
    case "restaurant":
    case "dining":
    case "meal":
      return HOTEL_DEPARTMENTS.roomDining;
    case "security":
      return HOTEL_DEPARTMENTS.security;
    case "emergency":
    case "urgent":
    case "sos":
    case "emergencyhelp":
    case "urgentstaffassistance":
      return HOTEL_DEPARTMENTS.emergency;
    case "traveldesk":
    case "travel":
    case "taxi":
    case "taxirequest":
    case "transport":
      return HOTEL_DEPARTMENTS.other;
    case "other":
      return HOTEL_DEPARTMENTS.other;
    default:
      return safeFallback;
  }
};

export const isAllDepartment = (value) =>
  normalizeDepartment(value, HOTEL_DEPARTMENTS.other) === HOTEL_DEPARTMENTS.all;

export const departmentDisplayName = (value) => {
  const department = normalizeDepartment(value);

  if (department === HOTEL_DEPARTMENTS.roomDining) return "Room dining";
  if (department === HOTEL_DEPARTMENTS.all) return "All departments";

  return department || "Department";
};

export const readableLabel = (value, fallback = "—") => {
  const text = safeText(value);

  if (!text) return fallback;

  return text
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
};

export const formatAlertTime = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-NP", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getAlertPriority = (item = {}) => {
  const rawType = firstText(
    item.type,
    item.Type,
    item.requestType,
    item.RequestType,
    item.serviceType,
    item.ServiceType
  );

  const rawPriority = firstText(item.priority, item.Priority);

  if (rawPriority.toLowerCase().includes("urgent")) {
    return ALERT_PRIORITIES.urgent;
  }

  if (rawPriority.toLowerCase().includes("high")) {
    return ALERT_PRIORITIES.high;
  }

  if (item.isOverdue || item.IsOverdue || item.isEscalated || item.IsEscalated) {
    return ALERT_PRIORITIES.high;
  }

  if (rawType.toLowerCase().includes("urgent")) {
    return ALERT_PRIORITIES.urgent;
  }

  return ALERT_PRIORITIES.normal;
};

export const normalizeRequestAlert = (item = {}) => {
  const room = item.room || item.Room || {};
  const status = firstText(
    item.status,
    item.Status,
    item.statusName,
    item.StatusName,
    "Pending"
  );

  const type = firstText(
    item.type,
    item.Type,
    item.requestType,
    item.RequestType,
    item.serviceType,
    item.ServiceType,
    item.requestTypeName,
    item.RequestTypeName,
    "Service request"
  );

  const id = firstText(item.id, item.Id, item.requestId, item.RequestId);
  const rawSourceType = firstText(item.sourceType, item.SourceType);
  const rawSourceId = firstText(item.sourceId, item.SourceId);
  const linkedOrderId = firstText(item.orderId, item.OrderId, rawSourceId);
  const rawDepartment = firstText(
    item.department,
    item.Department,
    item.departmentName,
    item.DepartmentName
  );

  const sourceToken = normalizeDepartmentToken(rawSourceType);

  if ((sourceToken === "order" || sourceToken === "posorder") && linkedOrderId) {
    return normalizeOrderAlert({
      ...item,
      id: linkedOrderId,
      Id: linkedOrderId,
      orderId: linkedOrderId,
      OrderId: linkedOrderId,
      linkedGuestRequestId: id,
      LinkedGuestRequestId: id,
      department: rawDepartment || HOTEL_DEPARTMENTS.roomDining,
      Department: rawDepartment || HOTEL_DEPARTMENTS.roomDining,
      requestType: "Room dining order",
      RequestType: "Room dining order",
      route: `/hotel/pos/orders/${linkedOrderId}`,
      Route: `/hotel/pos/orders/${linkedOrderId}`,
    });
  }

  const department = rawDepartment
    ? normalizeDepartment(rawDepartment)
    : normalizeDepartment(type, HOTEL_DEPARTMENTS.reception);

  const roomNumber = firstText(
    item.roomNumber,
    item.RoomNumber,
    item.roomName,
    item.RoomName,
    room.roomNumber,
    room.RoomNumber,
    room.name,
    room.Name,
    "—"
  );

  const createdAt = firstText(
    item.createdAt,
    item.CreatedAt,
    item.createdAtUtc,
    item.CreatedAtUtc,
    item.requestedAtUtc,
    item.RequestedAtUtc,
    item.timestamp,
    item.Timestamp,
    new Date().toISOString()
  );

  const description = firstText(
    item.notes,
    item.Notes,
    item.note,
    item.Note,
    item.reminder,
    item.Reminder,
    item.description,
    item.Description,
    item.message,
    item.Message,
    item.body,
    item.Body
  );

  const isReminder = Boolean(
    item.isReminder ||
    item.IsReminder ||
    item.eventType === "guest.request.reminder" ||
    (item.reminderCount && Number(item.reminderCount) > 0) ||
    item.hasReminder ||
    item.HasReminder
  );
  const reminderCount = Number(item.reminderCount || item.ReminderCount || (isReminder ? 1 : 0));

  const baseTitle = firstText(item.title, item.Title, readableLabel(type, "Service request"));
  const title = isReminder
    ? (baseTitle.toLowerCase().startsWith("reminder")
        ? baseTitle
        : `Reminder${reminderCount > 0 ? ` #${reminderCount}` : ""}: ${baseTitle}`)
    : baseTitle;

  return {
    id: isReminder
      ? `request-${id || createdAt}-reminder${reminderCount > 0 ? `-${reminderCount}` : ""}`
      : `request-${id || createdAt}`,
    notificationEventId: firstText(
      item.notificationEventId,
      item.NotificationEventId
    ),
    sourceId: id,
    sourceType: "request",
    title,
    roomNumber,
    guestName: firstText(
      item.guestName,
      item.GuestName,
      item.guestSessionName,
      item.GuestSessionName,
      item.guest,
      item.Guest,
      "Guest"
    ),
    department,
    departmentLabel: departmentDisplayName(department),
    requestType: readableLabel(type, "Service request"),
    priority: isReminder ? ALERT_PRIORITIES.high : getAlertPriority(item),
    isReminder,
    reminderCount,
    createdAt,
    status: readableLabel(status, "Pending"),
    description,
    route: id ? `/hotel/requests/${id}` : "/hotel/requests",
  };
};

export const normalizeOrderAlert = (order = {}) => {
  const id = firstText(
    order.orderId,
    order.OrderId,
    order.sourceId,
    order.SourceId,
    order.id,
    order.Id
  );

  const linkedGuestRequestId =
    firstText(
      order.linkedGuestRequestId,
      order.LinkedGuestRequestId,
      order.guestRequestId,
      order.GuestRequestId
    ) || null;

  const department = normalizeDepartment(
    firstText(order.department, order.Department, HOTEL_DEPARTMENTS.roomDining),
    HOTEL_DEPARTMENTS.roomDining
  );

  const createdAt = firstText(
    order.createdAt,
    order.CreatedAt,
    order.createdAtUtc,
    order.CreatedAtUtc,
    new Date().toISOString()
  );

  const total = Number(order.total || order.Total || 0);
  const description = firstText(
    order.body,
    order.Body,
    order.description,
    order.Description,
    order.message,
    order.Message,
    total ? `Total NPR ${total.toFixed(2)}` : ""
  );

  return {
    id: `order-${id || createdAt}`,
    notificationEventId: firstText(
      order.notificationEventId,
      order.NotificationEventId
    ),
    sourceId: id,
    sourceType: "order",
    linkedGuestRequestId,
    title: firstText(
      order.title,
      order.Title,
      order.orderName,
      order.OrderName,
      "Room dining order"
    ),
    roomNumber: firstText(
      order.roomName,
      order.RoomName,
      order.roomNumber,
      order.RoomNumber,
      order.tableName,
      order.TableName,
      "—"
    ),
    guestName: firstText(
      order.clientName,
      order.ClientName,
      order.customerName,
      order.CustomerName,
      order.guestName,
      order.GuestName,
      "Guest"
    ),
    department,
    departmentLabel: departmentDisplayName(department),
    requestType: firstText(
      order.requestType,
      order.RequestType,
      "Room dining order"
    ),
    priority: ALERT_PRIORITIES.normal,
    createdAt,
    status: readableLabel(firstText(order.status, order.Status, "Open"), "Open"),
    description,
    route:
      firstText(order.route, order.Route, order.url, order.Url) ||
      (id ? `/hotel/pos/orders/${id}` : "/hotel/pos/orders"),
  };
};

export const normalizeCallAlert = (payload = {}) => {
  const callId = firstText(
    payload.callId,
    payload.CallId,
    payload.sourceId,
    payload.SourceId,
    Date.now()
  );

  const rawDepartment = firstText(
    payload.department,
    payload.Department,
    payload.targetDepartment,
    payload.TargetDepartment,
    "Reception"
  );

  const department = normalizeDepartment(rawDepartment);
  const departmentLabel = departmentDisplayName(department);

  const rawCallerDepartment = firstText(
    payload.callerDepartment,
    payload.CallerDepartment
  );

  const callerDepartment = rawCallerDepartment
    ? normalizeDepartment(rawCallerDepartment)
    : "";

  const callerDepartmentLabel = callerDepartment
    ? departmentDisplayName(callerDepartment)
    : "";

  const callerName = firstText(
    payload.guestName,
    payload.GuestName,
    payload.callerName,
    payload.CallerName,
    payload.staffName,
    payload.StaffName,
    "Caller"
  );

  return {
    id: `call-${callId}`,
    sourceId: callId,
    sourceType: "call",
    title: firstText(payload.title, payload.Title, `Incoming ${departmentLabel} call`),
    roomNumber: firstText(payload.roomName, payload.RoomName, "—"),
    guestName: callerName,
    department,
    departmentLabel,
    requestType: "Guest call",
    priority: ALERT_PRIORITIES.urgent,
    createdAt: new Date().toISOString(),
    status: "New",
    description: callerDepartment
      ? `${callerDepartmentLabel} is calling ${departmentLabel}.`
      : `${callerName} is calling ${departmentLabel}.`,
    route:
      firstText(payload.route, payload.Route, payload.url, payload.Url) ||
      `/hotel/calls/incoming/${callId}`,
  };
};

export const normalizeDepartmentCallAlert = (payload = {}) => {
  const sourceId = firstText(
    payload.sourceId,
    payload.SourceId,
    payload.id,
    payload.Id,
    Date.now()
  );

  const department = normalizeDepartment(
    firstText(payload.department, payload.Department, HOTEL_DEPARTMENTS.reception)
  );

  const departmentLabel = departmentDisplayName(department);

  const createdAt = firstText(
    payload.createdAt,
    payload.CreatedAt,
    payload.createdAtUtc,
    payload.CreatedAtUtc,
    payload.timestamp,
    payload.Timestamp,
    new Date().toISOString()
  );

  return {
    id: `department-call-${sourceId}`,
    sourceId,
    sourceType: "department-call",
    title: firstText(payload.title, payload.Title, `${departmentLabel} alert`),
    roomNumber: firstText(payload.roomName, payload.RoomName, "—"),
    guestName: firstText(
      payload.requestedByName,
      payload.RequestedByName,
      "Staff"
    ),
    department,
    departmentLabel,
    requestType: firstText(
      payload.requestType,
      payload.RequestType,
      "Department call"
    ),
    priority: getAlertPriority(payload),
    createdAt,
    status: firstText(payload.status, payload.Status, "New"),
    description: firstText(
      payload.message,
      payload.Message,
      payload.body,
      payload.Body,
      payload.description,
      payload.Description
    ),
    route:
      firstText(payload.route, payload.Route, payload.url, payload.Url) ||
      "/hotel/calls",
  };
};

export const normalizePushAlert = (payload = {}) => {
  const data = payload.data || payload.Data || {};
  const merged = { ...payload, ...data };

  const rawSourceType = firstText(
    payload.sourceType,
    payload.SourceType,
    merged.sourceType,
    merged.SourceType,
    payload.type,
    payload.Type
  ).toLowerCase();

  if (rawSourceType.includes("call")) return normalizeCallAlert(merged);
  if (rawSourceType === "order" || rawSourceType === "posorder") {
    return normalizeOrderAlert(merged);
  }
  if (rawSourceType === "department-call") {
    return normalizeDepartmentCallAlert(merged);
  }

  return normalizeRequestAlert(merged);
};

const recentAlertDispatches = new Map();

export const dispatchDepartmentAlert = (alert) => {
  if (!alert?.id) return;

  const now = Date.now();
  const lastDispatchedAt = recentAlertDispatches.get(alert.id);

  if (lastDispatchedAt && now - lastDispatchedAt < 8000) {
    return;
  }

  recentAlertDispatches.set(alert.id, now);

  recentAlertDispatches.forEach((value, key) => {
    if (now - value > 30000) recentAlertDispatches.delete(key);
  });

  window.dispatchEvent(new CustomEvent(ALERT_EVENT_NAME, { detail: alert }));
};

let alertAudioContext = null;

export const playDepartmentAlertSound = () => {
  if (typeof window === "undefined") return;

  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;

    alertAudioContext = alertAudioContext || new AudioContext();
    const context = alertAudioContext;

    const volume = Math.min(
      1,
      Math.max(
        0.05,
        Number(localStorage.getItem("hotelDepartmentAlertVolume") || 80) / 100
      )
    );

    const playTone = (frequency, startOffset, duration, peak = 0.16) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const start = context.currentTime + startOffset;

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, start);

      gain.gain.setValueAtTime(0.001, start);
      gain.gain.exponentialRampToValueAtTime(peak * volume, start + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, start + duration);

      oscillator.connect(gain);
      gain.connect(context.destination);

      oscillator.start(start);
      oscillator.stop(start + duration + 0.03);
    };

    playTone(784, 0, 0.22, 0.15);
    playTone(988, 0.25, 0.22, 0.17);
    playTone(784, 0.5, 0.28, 0.15);
  } catch {
    // Browsers can block audio before user interaction; alert UI still works.
  }
};

export const showBrowserAlertNotification = (alert) => {
  if (
    typeof window === "undefined" ||
    window.Notification?.permission !== "granted"
  ) {
    return;
  }

  if (document.visibilityState === "visible") {
    return;
  }

  const room =
    alert.roomNumber && alert.roomNumber !== "—"
      ? `Room ${alert.roomNumber}`
      : "Hotel alert";

  const notification = new Notification(`${safeText(alert.title)} - ${room}`, {
    body: `${departmentDisplayName(alert.department)} · ${safeText(
      alert.requestType,
      "Request"
    )}`,
    icon: "/pwa-icon.svg",
    tag: alert.id,
    requireInteraction: alert.priority === ALERT_PRIORITIES.urgent,
  });

  notification.onclick = () => {
    window.focus?.();
    window.location.assign(alert.route || "/hotel/calls");
    notification.close?.();
  };
};